import { useEffect, useMemo, useState } from 'react';
import { usePayComContext } from '@/lib/paycom/context';
import type { SavedBankInstrument } from '@/lib/paycom/types';
import { WithdrawalBankPicker } from './WithdrawalBankPicker';
import { WithdrawalBankSetup } from './WithdrawalBankSetup';

interface Props {
  onSelectionChange: (account: SavedBankInstrument | null) => void;
  mode: 'pick' | 'setup';
  onRequestAddNew: () => void;
  onCancelAddNew: () => void;
}

/**
 * Owns bank-mode state for the withdraw flow. Mirror of CardWithdrawPanel
 * but for `us_bank_account` instruments — separate Pay.com surface,
 * separate dto fields.
 */
export function BankWithdrawPanel({ onSelectionChange, mode, onRequestAddNew, onCancelAddNew }: Props) {
  const { state: payCom } = usePayComContext();

  // The user's explicit pick (or deselect). While unset, the selection
  // DERIVES to the default (first) vault account so the method screen opens
  // ready to confirm (ARC-150) — no setState-in-effect needed.
  const [explicitPick, setExplicitPick] = useState<{ account: SavedBankInstrument | null } | null>(null);
  const [localBanks, setLocalBanks] = useState<SavedBankInstrument[]>([]);

  const mergedBanks = useMemo<SavedBankInstrument[]>(() => {
    const all = [...localBanks, ...payCom.savedBankAccounts];
    const seen = new Set<string>();
    return all.filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));
  }, [localBanks, payCom.savedBankAccounts]);

  const selectedBank = explicitPick ? explicitPick.account : (mergedBanks[0] ?? null);

  // Keep the parent's mirror (used by the submit payload) in sync with the
  // derived selection — including the auto-selected default.
  useEffect(() => {
    onSelectionChange(selectedBank);
  }, [selectedBank, onSelectionChange]);

  function handleSelect(account: SavedBankInstrument | null) {
    setExplicitPick({ account });
  }

  function handleSaved(account: SavedBankInstrument) {
    setLocalBanks((prev) => (prev.some((b) => b.id === account.id) ? prev : [account, ...prev]));
    setExplicitPick({ account });
    onCancelAddNew();
  }

  function handleAddNew() {
    onRequestAddNew();
  }

  if (mode === 'setup') {
    return <WithdrawalBankSetup onSaved={handleSaved} onCancel={onCancelAddNew} />;
  }

  return (
    <WithdrawalBankPicker
      accounts={mergedBanks}
      selectedId={selectedBank?.id ?? null}
      onSelect={handleSelect}
      onAddNew={handleAddNew}
    />
  );
}
